import { html } from "lit-html"

import ActionMenu from "../../generic/action-menu"
import { slugify, facebookUrl, twitterUrl } from "../../utils"
import {
  CALENDAR_PATH,
  CLONE_PATH,
  CREATE_ASSIGNMENT_PATH,
  CREATE_ASSIGNMENT_MEETING_PARAM,
  EDIT_PATH,
} from "../../constants"

const ShareMenu = (url, expanded) => {
  return ActionMenu({
    switcher: html`
      <a
        class="inline-block ml-4 h-9 w-9 flex justify-center items-center ${expanded
          ? "button secondary"
          : "text-gray-450"}"
        aria-label="Share"
        href="javascript:void(0)"
      >
        <span class="tooltip-icon h-7">
          <i class="cb fa-share-nodes h-7"></i>
          <span class="tooltip-icon-tooltiptext">Share</span>
        </span>
      </a>
    `,
    children: html`
      <a
        class="dropdown-item flex items-center"
        role="menuitem"
        href=${facebookUrl(url)}
        target="_blank"
        rel="noopener noreferrer"
        @click=${(e) => e.stopPropagation()}
      >
        <i class="fab fa-facebook mr-4 text-[#1777f2]"></i>
        <span>Share on Facebook</span>
      </a>
      <a
        class="dropdown-item flex items-center"
        role="menuitem"
        href=${twitterUrl(url)}
        target="_blank"
        rel="noopener noreferrer"
        @click=${(e) => e.stopPropagation()}
      >
        <i class="fab fa-twitter mr-4 text-blue-twitter"></i>
        <span>Share on Twitter</span>
      </a>
    `,
  })
}

const CalendarMenu = (
  url,
  iconIcalSrc,
  iconGoogleCalendarSrc,
  gcal_link,
  expanded
) => {
  return ActionMenu({
    switcher: html`
      <a
        class="inline-block ml-4 h-9 w-9 flex justify-center items-center ${expanded
          ? "button secondary"
          : "text-gray-450"}"
        aria-label="Add to calendar"
        href="javascript:void(0)"
      >
        <span class="tooltip-icon h-7">
          <i class="far fa-calendar-day h-7"></i>
          <span class="tooltip-icon-tooltiptext">Add to calendar</span>
        </span>
      </a>
    `,
    children: html`
      <a
        class="dropdown-item flex items-center"
        role="menuitem"
        href="${url}${CALENDAR_PATH}"
        @click=${(e) => e.stopPropagation()}
      >
        <img class="mr-4" src="${iconIcalSrc}" alt="iCal" />
        <span>iCal</span>
      </a>
      <a
        class="dropdown-item flex items-center"
        role="menuitem"
        href="${gcal_link}"
        target="_blank"
        rel="noopener noreferrer"
        @click=${(e) => e.stopPropagation()}
      >
        <img
          class="mr-4"
          src="${iconGoogleCalendarSrc}"
          alt="Google Calendar"
        />
        <span>Google Calendar</span>
      </a>
    `,
  })
}

export const TagList = (tags, cssStyle = "") =>
  tags.length
    ? html`<div class="${cssStyle}">
        ${tags.map(
          (tag, idx) => html`
            <span class="inline-block text-gray-450">
              ${tag.toUpperCase()}${idx < tags.length - 1
                ? html`&nbsp; &#x2022; &nbsp;`
                : ``}
            </span>
          `
        )}
      </div>`
    : ``

export const ActionButtons = ({
  id,
  url,
  can_edit,
  can_share,
  can_clone,
  can_view,
  can_convert_to_assignment,
  iconIcalSrc,
  iconGoogleCalendarSrc,
  gcal_link,
  expanded,
}) => {
  return html` <div class="flex items-center">
    ${can_edit
      ? html` <a
          class="inline-block ml-4 h-9 w-9 flex justify-center items-center ${expanded
            ? "button secondary"
            : "text-gray-450"}"
          aria-label="Edit"
          href=${`${url}${EDIT_PATH}`}
          onclick="event.stopPropagation();"
        >
          <span class="tooltip-icon h-7">
            <i class="fas fa-pen h-[18px]"></i>
            <span class="tooltip-icon-tooltiptext">Edit</span>
          </span>
        </a>`
      : ``}
    ${can_share ? ShareMenu(url, expanded) : ``}
    ${CalendarMenu(
      url,
      iconIcalSrc,
      iconGoogleCalendarSrc,
      gcal_link,
      expanded
    )}
    ${can_clone
      ? html` <a
          class="inline-block ml-4 h-9 w-9 flex justify-center items-center ${expanded
            ? "button secondary"
            : "text-gray-450"}"
          aria-label="Duplicate"
          href=${`${url}${CLONE_PATH}`}
          onclick="event.stopPropagation();"
        >
          <span class="tooltip-icon h-7">
            <i class="far fa-clone h-7"></i>
            <span class="tooltip-icon-tooltiptext">Duplicate</span>
          </span>
        </a>`
      : ``}
    ${can_convert_to_assignment
      ? html`<a
          class="inline-block ml-4 h-9 px-5 flex justify-center items-center button secondary w-full"
          href="${CREATE_ASSIGNMENT_PATH}?${CREATE_ASSIGNMENT_MEETING_PARAM}=${id}"
          onclick="event.stopPropagation();"
        >
          <span class="font-bold">Create Assignment</span>
        </a>`
      : ``}
    ${can_view && expanded
      ? html`<a
          class="inline-block ml-4 h-9 px-5 flex justify-center items-center button secondary w-full"
          href=${url}
          onclick="event.stopPropagation();"
        >
          <span class="font-bold">View details</span>
        </a>`
      : ``}
  </div>`
}

export const HeaderSection = (
  {
    status,
    tags,
    id,
    url,
    can_edit,
    can_share,
    can_clone,
    can_view,
    can_convert_to_assignment,
    iconIcalSrc,
    iconGoogleCalendarSrc,
    gcal_link,
  },
  expanded = false
) => {
  return html`
    <div class="flex flex-wrap justify-between items-center">
      <div class="mb-5 leading-[1.75]">
        ${status
          ? html`<div
              class="filter-result-status ${slugify(status)} mr-4 inline"
            >
              ${status}
            </div>`
          : ``}
        ${TagList(tags, "inline")}
      </div>
      <div class="ml-auto mb-5">
        ${ActionButtons({
          id,
          url,
          can_edit,
          can_share,
          can_clone,
          can_view,
          can_convert_to_assignment,
          iconIcalSrc,
          iconGoogleCalendarSrc,
          gcal_link,
          expanded,
        })}
      </div>
    </div>
  `
}
